// Hooks
import { useQuery } from '@hooks/useQuery';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useUserQuery, useUserMutation } from '@features/redux/apis/auth';

// Base Components
import { Grid, Typography, Box, Tooltip, FormControlLabel, Checkbox } from '@mui/material';

// Design System Components
import { EntityPageLayout, EntityPageHeader, InfoCard, DraftTextField, TextField } from '@components/design-system';
import { colors, typography as typographyTokens } from '@components/design-system/tokens';
import PersonIcon from '@mui/icons-material/Person';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Fixed Scope Components
import { MultiOrgDropdowns } from '@components/forms/dropdowns/MultiOrgDropdowns';
import { UserEffects } from './UserEffects';
import { ResetPassword } from './ResetPassword';

// Utils, Consts
import { Operation } from '@/types/general';
import { Query } from '@features/redux/apis/utils';
import { AppRoutes } from '@consts/routes';
import { Validate } from '@utils/validate';

// Types
import type { User as UserType } from '@features/redux/apis/auth';
import { store, type RootState } from '@features/redux/store';
import type { Option } from '@components/forms/dropdowns/base/FormMultiSelect';

// Default user state for creating new users
const defaultUserState: UserType = {
    given_name: '',
    family_name: '',
    email: '',
    password: '',
    'custom:PriceEditRole': '0',
    'custom:UploaderRole': '0',
    'custom:UserEditRole': '0',
    'custom:ProductEditRole': '0',
    'custom:StoreEditRole': '0',
    'custom:Notes': '',
    'custom:Companies': '',
    'custom:Locations': '',
    'custom:Resellers': '',
    Username: '',
    Enabled: true,
    UserStatus: '',
    sub: '',
    'custom:userType': '',
};

const useUser = Query.withDraft(useUserQuery, defaultUserState, (fetched, init) => {
    // If fetched data exists, use it; otherwise use the initial state
    return fetched || init || defaultUserState;
});

// Create a wrapper to ensure draft state is never undefined
const useUserWithNonNullableDraft = (args: Parameters<typeof useUser>[0], options?: Parameters<typeof useUser>[1]) => {
    const result = useUser(args, options);
    const [draftState, setDraftState] = result.draft;

    // Ensure draft state is never undefined
    const safeDraftState = draftState || defaultUserState;

    // Create a wrapped setter that ensures we never set undefined
    const safeSetter: React.Dispatch<React.SetStateAction<UserType>> = (action) => {
        if (typeof action === 'function') {
            setDraftState((prev) => action(prev || defaultUserState));
        } else {
            setDraftState(action);
        }
    };

    const safeDraft: [UserType, typeof safeSetter] = [safeDraftState, safeSetter];

    return {
        ...result,
        draft: safeDraft,
    };
};

export const UserPage = () => {
    const navigate = useNavigate();
    const role = useSelector((state: RootState) => state.auth.Roles.role);

    const userId = useQuery().get('id');
    const operation = userId ? Operation.Edit : Operation.Create;

    const { data: user, draft, isLoading } = useUserWithNonNullableDraft({ id: userId! }, { skip: !userId });
    const [draftState, setDraftState] = draft;

    const [mutate, { isLoading: mutationLoading }] = useUserMutation({
        fixedCacheKey: operation === Operation.Edit ? 'UserMutation' : 'UserCreation',
    });

    const selectedState = useState<Record<'companies' | 'resellers' | 'locations', Option[]>>({
        companies: [],
        resellers: [],
        locations: [],
    });

    const [selected] = selectedState;

    const pageTitle = operation === Operation.Create 
        ? 'Create New User' 
        : `${draftState.given_name} ${draftState.family_name}`.trim() || 'Edit User';

    const isValid =
        Validate.Auth.user(draftState) &&
        Object.values(selected).every((arr) => Boolean(arr.length)) &&
        (operation === Operation.Edit || Validate.Auth.password(draftState.password!));

    const handleSave = () => {
        const userToken = store.getState().auth.TokenPayload;

        const companies = selected.companies.map(({ id }) => id).join(',');
        const locations = selected.locations.map(({ id }) => id).join(',');
        const resellers = selected.resellers.map(({ id }) => id).join(',');

        const userPayload: UserType = {
            ...defaultUserState,
            ...draftState,
            Username: draftState.email || '',
            // Ensure required fields are not undefined
            given_name: draftState.given_name || '',
            family_name: draftState.family_name || '',
            email: draftState.email || '',
            // ? If "All" was selected, give the user all the companies/locations/resellers
            // ? the creating user has.
            'custom:Companies': companies === '*' ? userToken!['custom:Companies']! : companies,
            'custom:Locations': locations === '*' ? userToken!['custom:Locations']! : locations,
            'custom:Resellers': resellers === '*' ? userToken!['custom:Resellers']! : resellers,
        };

        mutate({
            method: operation === Operation.Create ? 'POST' : 'PUT',
            user: userPayload,
        });
    };

    return (
        <>
            <UserEffects />

            <EntityPageLayout loading={isLoading && !user}>
                {/* Header - No ID shown */}
                <EntityPageHeader
                    entityType='company'
                    title={pageTitle}
                    icon={<PersonIcon />}
                    isCreate={operation === Operation.Create}
                    loading={isLoading && !user}
                    onCancel={() => navigate(AppRoutes.Users)}
                    onSave={handleSave}
                    saveDisabled={!isValid}
                    saving={mutationLoading}
                />

                {/* User Details Card */}
                <InfoCard title='User Details' icon={<InfoOutlinedIcon />}>
                    <Grid container spacing={2}>
                        <DraftTextField
                    label='First Name'
                            stateKey='given_name'
                    draft={draft}
                            validateFn={Validate.name}
                    helperText={{
                        valid: '',
                        invalid: 'Please provide a valid first name.',
                    }}
                            size={6}
                            required
                />

                        <DraftTextField
                    label='Last Name'
                            stateKey='family_name'
                    draft={draft}
                            validateFn={Validate.name}
                    helperText={{
                        valid: '',
                        invalid: 'Please provide a valid last name.',
                    }}
                            size={6}
                            required
                        />

                        <DraftTextField
                            label='Email Address'
                            stateKey='email'
                            draft={draft}
                            type='email'
                            validateFn={Validate.Auth.email}
                    helperText={{
                        valid: '',
                                invalid: 'Please enter a valid email address',
                    }}
                            size={operation === Operation.Edit ? 8 : 6}
                    disabled={operation === Operation.Edit}
                            required
                />

                {operation === Operation.Edit && <ResetPassword user={user!} />}

                {operation === Operation.Create && (
                            <DraftTextField
                                label='Password'
                                stateKey='password'
                                draft={draft}
                                type='password'
                                validateFn={Validate.Auth.password}
                        helperText={{
                            valid: '',
                                    invalid: 'Password must be at least 6 characters',
                        }}
                                size={6}
                                required
                            />
                        )}
                    </Grid>
                </InfoCard>

                {/* Organization Access Card */}
                <InfoCard title='Organization Access'>
                    <Grid container spacing={2}>
                <MultiOrgDropdowns
                    state={selectedState}
                    populate={{
                        companies: user?.['custom:Companies'],
                        resellers: user?.['custom:Resellers'],
                        locations: user?.['custom:Locations'],
                    }}
                />
                    </Grid>
                </InfoCard>

                {/* Permissions Card */}
                <InfoCard title='Permissions'>
                                <Typography
                        variant='body2'
                                    sx={{
                            color: colors.neutral[500],
                            fontSize: typographyTokens.fontSize.sm,
                            mb: 2,
                                    }}>
                                    These permissions control features and data access
                                </Typography>

                    <Grid container spacing={2}>
                        {role.users.edit && (
                            <Grid item xs={12} sm={6}>
                                <Tooltip
                                    title='Grants access to manage user accounts, create new users, edit user permissions, and view user activity'
                                    arrow
                                    placement='top'>
                                    <FormControlLabel
                                        sx={{ userSelect: 'none', ml: 0 }}
                                        checked={draftState['custom:UserEditRole'] === '1'}
                                        onChange={(_, checked) => {
                                            setDraftState((prev) => ({
                                                ...prev,
                                                'custom:UserEditRole': checked ? '1' : '0',
                                            }));
                                        }}
                                        control={
                                            <Checkbox
                                                sx={{
                                                    color: colors.neutral[400],
                                                    '&.Mui-checked': {
                                                        color: colors.primary[500],
                                                    },
                                            }}
                                        />
                                        }
                                        label={
                                            <Typography
                                                variant='body2'
                                                sx={{ color: colors.neutral[700], fontWeight: 500 }}>
                                                Manage Users
                                            </Typography>
                                        }
                                    />
                                </Tooltip>
                            </Grid>
                        )}

                        {role.stores.edit && (
                            <Grid item xs={12} sm={6}>
                                <Tooltip
                                    title='Grants access to manage store information, configure store settings, configure system settings, and view organization data'
                                    arrow
                                    placement='top'>
                                    <FormControlLabel
                                        sx={{ userSelect: 'none', ml: 0 }}
                                        checked={draftState['custom:StoreEditRole'] === '1'}
                                        onChange={(_, checked) => {
                                            setDraftState((prev) => ({
                                                ...prev,
                                                'custom:StoreEditRole': checked ? '1' : '0',
                                            }));
                                        }}
                                        control={
                                            <Checkbox
                                                sx={{
                                                    color: colors.neutral[400],
                                                    '&.Mui-checked': {
                                                        color: colors.primary[500],
                                                    },
                                            }}
                                        />
                                        }
                                        label={
                                            <Typography
                                                variant='body2'
                                                sx={{ color: colors.neutral[700], fontWeight: 500 }}>
                                                Manage Locations and Systems
                                            </Typography>
                                        }
                                    />
                                </Tooltip>
                            </Grid>
                        )}
                    </Grid>
                </InfoCard>

                {/* Internal Permissions Card (Super Admin Only) */}
                    {role.isSuperAdmin && (
                    <InfoCard
                        title='Internal Permissions'
                                sx={{
                            borderLeft: `4px solid ${colors.primary[500]}`,
                                }}>
                                    <Typography
                            variant='body2'
                                        sx={{
                                color: colors.primary[600],
                                fontSize: typographyTokens.fontSize.sm,
                                mb: 2,
                                        }}>
                                        These permissions are restricted to internal users only
                                    </Typography>

                        <Grid container spacing={2}>
                            {role.prices.edit && (
                                <Grid item xs={12} sm={6}>
                                    <Tooltip
                                        title='Grants access to billing features including invoices, batch processing, and billing changes management'
                                        arrow
                                        placement='top'>
                                        <FormControlLabel
                                            sx={{ userSelect: 'none', ml: 0 }}
                                                disabled={Object.values(selected).some((item) => item?.[0]?.id !== '*')}
                                            checked={draftState['custom:PriceEditRole'] === '1'}
                                            onChange={(_, checked) => {
                                                setDraftState((prev) => ({
                                                    ...prev,
                                                    'custom:PriceEditRole': checked ? '1' : '0',
                                                }));
                                            }}
                                            control={
                                                <Checkbox
                                                    sx={{
                                                        color: colors.neutral[400],
                                                        '&.Mui-checked': {
                                                            color: colors.primary[500],
                                                        },
                                                }}
                                            />
                                            }
                                            label={
                                                <Typography
                                                    variant='body2'
                                                    sx={{ color: colors.neutral[700], fontWeight: 500 }}>
                                                    Billing Management
                                                </Typography>
                                            }
                                        />
                                    </Tooltip>
                                </Grid>
                            )}

                            {role.uploading.edit && (
                                <Grid item xs={12} sm={6}>
                                    <Tooltip
                                        title='Grants access to design management including uploading models, managing manufacturers, and configuring coverages'
                                        arrow
                                        placement='top'>
                                        <FormControlLabel
                                            sx={{ userSelect: 'none', ml: 0 }}
                                                disabled={Object.values(selected).some((item) => item?.[0]?.id !== '*')}
                                            checked={draftState['custom:UploaderRole'] === '1'}
                                            onChange={(_, checked) => {
                                                setDraftState((prev) => ({
                                                    ...prev,
                                                    'custom:UploaderRole': checked ? '1' : '0',
                                                }));
                                            }}
                                            control={
                                                <Checkbox
                                                    sx={{
                                                        color: colors.neutral[400],
                                                        '&.Mui-checked': {
                                                            color: colors.primary[500],
                                                        },
                                                }}
                                            />
                                            }
                                            label={
                                                <Typography
                                                    variant='body2'
                                                    sx={{ color: colors.neutral[700], fontWeight: 500 }}>
                                                    Edit Designs
                                                </Typography>
                                            }
                                        />
                                    </Tooltip>
                                </Grid>
                            )}
                        </Grid>
                    </InfoCard>
                )}

                {/* Notes Card (Admin Only) */}
                {role.isAdmin && (
                    <InfoCard title='Internal Notes'>
                        <TextField
                            label='Notes'
                            value={draftState['custom:Notes'] || ''}
                            onChange={(value) => {
                                setDraftState((prev) => ({
                                    ...prev,
                                    'custom:Notes': value,
                                }));
                            }}
                            multiline
                            rows={3}
                            helperText='Notes visible to admins only'
                    />
                    </InfoCard>
                )}
            </EntityPageLayout>
        </>
    );
};
